import React, { FC, useEffect, useState, RefObject } from 'react';
import { t } from '../../../essentials/i18n';
import {
  Modal,
  CustomModalLayout,
  Text,
  Heading,
  Layout,
  Box,
  Cell,
  Avatar,
} from '@wix/design-system';
import { useExperiments } from '@wix/wix-experiments-react';
import { getBodyContentByOrigin } from './utils';
import {
  DeleteAppOriginSource,
  dataHooks,
  KEYS,
  KEYS_SITE_BRANCH,
  STUDIO_SITE_BRANCHES_EXPERIMENT,
  CLASSIC_TO_STUDIO_EXPERIMENT,
} from './constants';
import { PanelResolveType } from '@wix/editor-platform-sdk-types';
import { LoadingState } from './LoadingState';

export type DeleteAppOrigin = {
  source: DeleteAppOriginSource;
  name?: string;
};

export interface DeleteAppModalProps {
  keepModalOpen?: boolean;
  origin: DeleteAppOrigin;
  appName: string;
  appIconUrl?: string;
  appNote?: React.ReactNode;
  openHelpCenter: () => void;
  onClose: (closeType: PanelResolveType) => void;
  displayFootnote?: boolean;
  parentRef?: RefObject<HTMLDivElement>;
  getBranches: () => Promise<string[]>;
}

export const DeleteAppModalContainer: FC<DeleteAppModalProps> = ({
  keepModalOpen = true,
  origin,
  appName,
  appIconUrl,
  appNote: appNoteFromProps = null,
  openHelpCenter,
  onClose,
  displayFootnote,
  parentRef,
  getBranches,
}) => {
  const { experiments } = useExperiments({ readOnly: true });
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [branchesAmount, setBranchesAmount] = useState<undefined | number>(
    undefined,
  );
  const experimentsEnabled =
    experiments.enabled(STUDIO_SITE_BRANCHES_EXPERIMENT) &&
    experiments.enabled(CLASSIC_TO_STUDIO_EXPERIMENT);
  const [isLoading, setIsLoading] = useState(experimentsEnabled);

  const isSiteBranchMode = branchesAmount !== undefined && branchesAmount >= 2;

  const headingTitleKey = isSiteBranchMode
    ? KEYS_SITE_BRANCH.headingTitle
    : KEYS.headingTitle;

  const bodyContent = isSiteBranchMode
    ? t(KEYS_SITE_BRANCH.bodyContent, { app_name: appName })
    : getBodyContentByOrigin(origin, appName);

  const deleteAppButtonKey = isSiteBranchMode
    ? KEYS_SITE_BRANCH.deleteAppButton
    : KEYS.deleteAppButton;

  const standardAppNote = (
    <Text size="small">
      {t(KEYS_SITE_BRANCH.appNote, { app_name: appName })}
    </Text>
  );

  const appNote = isSiteBranchMode ? standardAppNote : appNoteFromProps;

  useEffect(() => {
    if (!keepModalOpen) {
      setIsModalOpen(false);
      onClose(PanelResolveType.CLOSE_ACTION);
    }
  }, [keepModalOpen, onClose]);

  useEffect(() => {
    if (experimentsEnabled) {
      getBranches()
        .then((res) => setBranchesAmount(res?.length ?? 1))
        .catch(() => setBranchesAmount(1))
        .finally(() => setIsLoading(false));
    } else {
      setBranchesAmount(1);
      setIsLoading(false);
    }
  }, [experiments, getBranches]);

  const onCloseButtonClick = () => {
    setIsModalOpen(false);
    onClose(PanelResolveType.CLOSE_ACTION);
  };

  const primaryButtonText = t(deleteAppButtonKey);

  const secondaryButtonText = t(KEYS.cancelButton);

  const secondaryButtonOnClick = () => {
    setIsModalOpen(false);
    onClose(PanelResolveType.SECONDARY_ACTION);
  };

  if (isLoading) {
    return (
      <LoadingState
        isModalOpen={isModalOpen}
        parentRef={parentRef}
        openHelpCenter={openHelpCenter}
        onCloseButtonClick={onCloseButtonClick}
        primaryButtonText={primaryButtonText}
        secondaryButtonText={secondaryButtonText}
        secondaryButtonOnClick={secondaryButtonOnClick}
      />
    );
  }
  return (
    <Modal
      dataHook={dataHooks.modalLayout}
      isOpen={isModalOpen}
      parentSelector={() => parentRef?.current || document.body}
    >
      <CustomModalLayout
        dataHook={dataHooks.customModalLayout}
        width="600px"
        maxHeight="calc(100vh - (48px*2))"
        theme={isSiteBranchMode ? 'standard' : 'destructive'}
        onHelpButtonClick={openHelpCenter}
        onCloseButtonClick={onCloseButtonClick}
        title={
          <Layout alignItems="center" gap="SP2">
            <Cell span={1}>
              <Avatar
                dataHook={dataHooks.avatar}
                shape="square"
                imgProps={{ src: appIconUrl }}
                size="size30"
              />
            </Cell>
            <Cell span={11}>
              <Heading
                dataHook={dataHooks.headingTitle}
                size="large"
                maxLines={2}
                ellipsis
              >
                {t(headingTitleKey, { app_name: appName })}
              </Heading>
            </Cell>
          </Layout>
        }
        content={
          <Layout gap="SP3">
            <Cell>
              <Text
                dataHook={dataHooks.bodyContent}
                size="medium"
                weight="thin"
              >
                {bodyContent}
              </Text>
            </Cell>
            <Cell>
              {appNote ? (
                <Box
                  dataHook={dataHooks.note}
                  backgroundColor="D75"
                  padding="SP2"
                  borderRadius={6}
                >
                  {appNote}
                </Box>
              ) : null}
            </Cell>
          </Layout>
        }
        primaryButtonText={primaryButtonText}
        primaryButtonOnClick={() => onClose(PanelResolveType.MAIN_ACTION)}
        secondaryButtonText={secondaryButtonText}
        secondaryButtonOnClick={secondaryButtonOnClick}
        footnote={
          displayFootnote ? (
            <Text dataHook={dataHooks.footnote} size="small">
              {t(KEYS.footnoteText)}{' '}
              <a
                href={t(KEYS.footnoteLinkUrl)}
                target="_blank"
                rel="noreferrer"
              >
                {t(KEYS.footnoteLinkText)}
              </a>
            </Text>
          ) : null
        }
        footnoteSkin="light"
      />
    </Modal>
  );
};
